<template>
  <div class="pa-4">
    <div class="d-flex align-center mb-4">
      <h2 class="text-h5 font-weight-bold">Cereri Documente</h2>
      <v-spacer />
      <v-btn color="red-darken-1" prepend-icon="mdi-file-plus" @click="dialogCerere = true">Cerere nouă</v-btn>
    </div>

    <v-tabs v-model="tab" color="red-darken-1" class="mb-4">
      <v-tab value="cereri">Cereri mele</v-tab>
      <v-tab value="tipuri">Tipuri disponibile</v-tab>
    </v-tabs>

    <v-window v-model="tab">
      <v-window-item value="cereri">
        <DxDataGrid :data-source="dataSource" :show-borders="true" :row-alternation-enabled="true" :remote-operations="true">
          <DxColumn data-field="DataCreare" caption="Data" data-type="datetime" format="dd.MM.yyyy" :width="130" />
          <DxColumn data-field="Stare" caption="Stare" :width="130" cell-template="stareCellTemplate" />
          <DxColumn data-field="Descriere" caption="Descriere" />
          <DxColumn data-field="ObsevatieRaspuns" caption="Răspuns HR" />

          <template #stareCellTemplate="{ data }">
            <v-chip :color="stareColor(data.data.Stare)" size="small" variant="tonal">{{ data.data.Stare }}</v-chip>
          </template>

          <DxPaging :page-size="15" />
          <DxFilterRow :visible="true" />
        </DxDataGrid>
      </v-window-item>

      <v-window-item value="tipuri">
        <v-row>
          <v-col cols="12" sm="6" md="4" v-for="tip in tipuri" :key="tip.ID">
            <v-card elevation="2" rounded="lg">
              <v-card-title>{{ tip.Denumire }}</v-card-title>
              <v-card-subtitle>{{ tip.Cod }}</v-card-subtitle>
              <v-card-text>
                <p class="text-body-2">{{ tip.Descriere }}</p>
                <v-chip v-if="tip.NecesitaFisierAngajat" size="small" color="blue" class="mr-1">Necesită fișier</v-chip>
                <v-chip v-if="tip.NecesitaAprobare" size="small" color="orange">Necesită aprobare</v-chip>
              </v-card-text>
              <v-card-actions>
                <v-btn size="small" color="red-darken-1" variant="tonal" @click="selectTip(tip)">Depune cerere</v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>
    </v-window>

    <!-- Dialog cerere nouă -->
    <v-dialog v-model="dialogCerere" max-width="500" persistent>
      <v-card rounded="xl">
        <v-card-title class="pa-4">
          <v-icon class="mr-2" color="red-darken-1">mdi-file-plus</v-icon>
          Cerere document
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4">
          <v-form v-model="cerereValid" ref="cerereForm">
            <v-select
              v-model="cerereForm.TipCerereDocumentID"
              :items="tipuri"
              item-title="Denumire"
              item-value="ID"
              label="Tip document"
              variant="outlined"
              density="compact"
              class="mb-3"
              :rules="[required]"
            />
            <v-textarea v-model="cerereForm.Descriere" label="Descriere / motivație" variant="outlined" density="compact" rows="3" class="mb-3" />
            <v-file-input
              v-if="tipSelectat?.NecesitaFisierAngajat"
              v-model="cerereFile"
              label="Fișier atașat"
              variant="outlined"
              density="compact"
              accept=".pdf,.doc,.docx,.jpg,.png"
            />
          </v-form>
          <v-alert v-if="cerereError" type="error" variant="tonal" class="mt-2">{{ cerereError }}</v-alert>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn variant="text" @click="dialogCerere = false">Anulează</v-btn>
          <v-spacer />
          <v-btn color="red-darken-1" :loading="cerereLoading" :disabled="!cerereValid" @click="submitCerere">Trimite</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { DxDataGrid, DxColumn, DxPaging, DxFilterRow } from 'devextreme-vue/data-grid'
import DataSource from 'devextreme/data/data_source'
import PORTAL_CerereDocument from '@/services/Portal/PORTAL_CerereDocument'
import PORTAL_TipCerereDocument from '@/services/Portal/PORTAL_TipCerereDocument'

export default defineComponent({
  name: 'CereriDocumente',
  components: { DxDataGrid, DxColumn, DxPaging, DxFilterRow },
  data() {
    return {
      tab: 'cereri',
      dataSource: new DataSource({ store: PORTAL_CerereDocument.getODataStore() }),
      tipuri: [] as any[],
      dialogCerere: false, cerereValid: false, cerereLoading: false,
      cerereError: '', cerereFile: null as File | null,
      cerereForm: { TipCerereDocumentID: '', Descriere: '' }
    }
  },
  computed: {
    tipSelectat(): any {
      return this.tipuri.find(t => t.ID === this.cerereForm.TipCerereDocumentID);
    }
  },
  async mounted() {
    const resp = await PORTAL_TipCerereDocument.getActive();
    this.tipuri = resp.data?.value ?? [];
  },
  methods: {
    required(v: any) { return !!v || 'Câmp obligatoriu' },
    stareColor(stare: string) {
      const map: Record<string, string> = { noua: 'blue', in_lucru: 'amber', finalizata: 'green', respinsa: 'red' };
      return map[stare] ?? 'grey';
    },
    selectTip(tip: any) {
      this.cerereForm.TipCerereDocumentID = tip.ID;
      this.dialogCerere = true;
    },
    async submitCerere() {
      this.cerereLoading = true; this.cerereError = '';
      try {
        if (this.cerereFile) {
          await PORTAL_CerereDocument.addWithFile(this.cerereForm.TipCerereDocumentID, this.cerereForm.Descriere, this.cerereFile);
        } else {
          await PORTAL_CerereDocument.add(this.cerereForm);
        }
        this.dialogCerere = false;
        this.dataSource.reload();
      } catch (e: any) {
        this.cerereError = e?.response?.data?.message ?? 'Eroare la trimitere.';
      } finally {
        this.cerereLoading = false;
      }
    }
  }
})
</script>
